import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { tickerApi } from '../services/tickerApi';
import { userTickerApi } from '../services/userTickerApi';
import { TickerDisplayData, TickerTableState, TickerSnapshotData } from '../types/ticker';

// Ticker names mapping for display
const TICKER_NAMES: Record<string, string> = {
  'NVDA': 'NVIDIA Corporation',
  'TSLA': 'Tesla, Inc.',
  'AAPL': 'Apple Inc.',
  'MSFT': 'Microsoft Corporation',
  'GOOGL': 'Alphabet Inc.',
  'AMZN': 'Amazon.com, Inc.',
  'META': 'Meta Platforms, Inc.',
  'NFLX': 'Netflix, Inc.',
};

// Default tickers to display
const DEFAULT_TICKERS = ['NVDA', 'AAPL', 'GOOGL', 'MSFT', 'AMZN', 'META', 'TSLA'];

// Helper function to check if all day.* values are 0
const areAllDayValuesZero = (day: { o: number; h: number; l: number; c: number; v: number }): boolean => {
  return day.o === 0 && day.h === 0 && day.l === 0 && day.c === 0 && day.v === 0;
};

// Helper function to fetch latest 2 ticker daily records for multiple tickers
const fetchBatchTickerDailyRecords = async (tickerSymbols: string[], isAuthenticated: boolean): Promise<Map<string, any[]>> => {
  try {
    const api = isAuthenticated ? userTickerApi : tickerApi;
    const response = await api.getBatchTickerDailyPrices(tickerSymbols, 2);
    
    // Convert array to Map for easy lookup by ticker symbol
    const recordsMap = new Map<string, any[]>();
    if (response.data) {
      response.data.forEach((tickerData: any) => {
        recordsMap.set(tickerData.ticker_symbol, tickerData.records || []);
      });
    }
    return recordsMap;
  } catch (error) {
    console.error(`Failed to fetch batch daily records for ${tickerSymbols.join(', ')}:`, error);
    return new Map();
  }
};

// Helper function to check if current time is in pre-market hours (4:00 AM - 9:30 AM NY time)
const isPreMarketHours = (): boolean => {
  const now = new Date();
  const nyTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const hours = nyTime.getHours();
  const minutes = nyTime.getMinutes();
  const timeInMinutes = hours * 60 + minutes;
  
  const preMarketStart = 4 * 60; // 4:00 AM
  const preMarketEnd = 9 * 60 + 30; // 9:30 AM
  
  return timeInMinutes >= preMarketStart && timeInMinutes < preMarketEnd;
};

// Helper function to check if current time is in after-hours (4:00 PM - 8:00 PM NY time)
const isAfterHours = (): boolean => {
  const now = new Date();
  const nyTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const hours = nyTime.getHours();
  const minutes = nyTime.getMinutes();
  const timeInMinutes = hours * 60 + minutes;
  
  const afterHoursStart = 16 * 60; // 4:00 PM
  const afterHoursEnd = 20 * 60; // 8:00 PM
  
  return timeInMinutes >= afterHoursStart && timeInMinutes < afterHoursEnd;
};

// Helper function to determine which record to use based on prevDay conditions
const determineFallbackRecord = (tickerData: TickerSnapshotData, dailyRecords: any[]): { prevClose: number; prevVolume: number } => {
  if (dailyRecords.length === 0) {
    // Fallback to original logic if no daily records available
    return {
      prevClose: tickerData.prevDay?.c || 0,
      prevVolume: tickerData.prevDay?.v || 0,
    };
  }

  const latestRecord = dailyRecords[0];
  const secondLatestRecord = dailyRecords[1];

  // Check if latest record fulfills the conditions
  const latestRecordMatches = latestRecord && 
    latestRecord.close_price_adjusted === tickerData.prevDay?.c &&
    latestRecord.open_price_adjusted === tickerData.prevDay?.o;

  if (latestRecordMatches && secondLatestRecord) {
    // Use second to last record
    return {
      prevClose: secondLatestRecord.close_price_adjusted || 0,
      prevVolume: secondLatestRecord.volume_adjusted || 0,
    };
  } else {
    // Use latest record
    return {
      prevClose: latestRecord.close_price_adjusted || 0,
      prevVolume: latestRecord.volume_adjusted || 0,
    };
  }
};

export const useTickerSnapshot = (tickers: string[] = DEFAULT_TICKERS, isAuthenticated: boolean = false, marketStatus?: string) => {
  const [state, setState] = useState<TickerTableState>({
    data: [],
    loading: false,
    error: null,
    lastUpdated: null,
  });

    // Use ref to store current tickers to avoid dependency issues
  const tickersRef = useRef<string[]>(tickers);
  const tickersString = useMemo(() => tickers.join(','), [tickers]);

  // Update ref when tickers change
  useEffect(() => {
    tickersRef.current = tickers;
  }, [tickers]);

  const fetchTickerData = useCallback(async () => {
    const currentTickers = tickersRef.current;
    const tickersToFetch = currentTickers.join(',');
    
    if (tickersToFetch.length === 0) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Check if using default tickers and not authenticated - use cached endpoint for landing page
      const isDefaultTickers = JSON.stringify([...currentTickers].sort()) === JSON.stringify([...DEFAULT_TICKERS].sort());
      const shouldUseCache = isDefaultTickers && !isAuthenticated;
      
      let response;
      if (shouldUseCache) {
        console.log('useTickerSnapshot: Using cached endpoint for default tickers');
        try {
          response = await tickerApi.landingCache.getCachedDaily({
            tickers: tickersToFetch,
          });
        } catch (cacheError) {
          console.warn('useTickerSnapshot: Cache failed, falling back to regular API:', cacheError);
          const api = isAuthenticated ? userTickerApi : tickerApi;
          response = await api.getTickerSnapshot({
            tickers: tickersToFetch,
          });
        }
      } else {
        const api = isAuthenticated ? userTickerApi : tickerApi;
        response = await api.getTickerSnapshot({
          tickers: tickersToFetch,
        });
      }

      // Check if any ticker has all day values as 0 to determine if we need batch data
      const tickersWithZeroDayValues = response.data.tickers.filter(ticker => areAllDayValuesZero(ticker.day));
      const tickerSymbols = tickersWithZeroDayValues.map(ticker => ticker.ticker);
      
      // Fetch batch daily records if needed
      let batchDailyRecords = new Map<string, any[]>();
      if (tickerSymbols.length > 0) {
        try {
          batchDailyRecords = await fetchBatchTickerDailyRecords(tickerSymbols, isAuthenticated);
        } catch (error) {
          console.error('Failed to fetch batch daily records:', error);
        }
      }

      // Transform API response to display data
      const displayData: TickerDisplayData[] = response.data.tickers.map((ticker) => {
        // Use current day data if available, otherwise fall back to previous day
        const currentClose = ticker.day.c > 0 ? ticker.day.c : (ticker.prevDay?.c || 0);
        const currentHigh = ticker.day.h > 0 ? ticker.day.h : (ticker.prevDay?.h || 0);
        const currentLow = ticker.day.l > 0 ? ticker.day.l : (ticker.prevDay?.l || 0);
        const currentVolume = ticker.day.v > 0 ? ticker.day.v : (ticker.prevDay?.v || 0);
        
        // Check if all day.* values are 0 to determine fallback strategy
        const allDayValuesZero = areAllDayValuesZero(ticker.day);
        
        // Handle open price with fallback logic
        let currentOpen = ticker.day.o > 0 ? ticker.day.o : (ticker.prevDay?.o || 0);
        if (allDayValuesZero) {
          const dailyRecords = batchDailyRecords.get(ticker.ticker) || [];
          if (dailyRecords.length > 0) {
            // Use open_price_adjusted from the latest ticker_daily record
            currentOpen = dailyRecords[0].open_price_adjusted || 0;
          }
        }
        
        let prevClose = ticker.prevDay?.c || 0;
        let prevVolume = ticker.prevDay?.v || 0;
        
        // If all day values are 0, use the new fallback logic with batch data
        if (allDayValuesZero) {
          const dailyRecords = batchDailyRecords.get(ticker.ticker) || [];
          const fallbackData = determineFallbackRecord(ticker, dailyRecords);
          prevClose = fallbackData.prevClose;
          prevVolume = fallbackData.prevVolume;
        }
        
        // Calculate change based on previous day's close price
        const change = currentClose - prevClose;
        const changePercent = prevClose > 0 ? (change / prevClose) * 100 : 0;

        // Calculate pre-market data (always based on prevDay.c)
        const inPreMarket = isPreMarketHours();
        const prevDayClose = ticker.prevDay?.c || 0;
        const preMarket = inPreMarket ? prevDayClose + ticker.todaysChange : 0;
        const preMarketChange = inPreMarket ? ticker.todaysChange : 0;
        const preMarketChangePercent = inPreMarket && prevDayClose > 0 ? (ticker.todaysChange / prevDayClose) * 100 : 0;

        // Calculate after-hours data (always based on prevDay.c)
        const inAfterHours = isAfterHours();
        const afterHours = inAfterHours ? prevDayClose + ticker.todaysChange : 0;
        const afterHoursChange = inAfterHours ? (prevDayClose + ticker.todaysChange - ticker.day.c) : 0;
        const afterHoursChangePercent = inAfterHours && ticker.day.c > 0 ? (afterHoursChange / ticker.day.c) * 100 : 0;

        return {
          ticker: ticker.ticker,
          name: TICKER_NAMES[ticker.ticker] || ticker.ticker,
          latest: currentClose,
          change: change,
          changePercent: changePercent,
          volume: currentVolume,
          high: currentHigh,
          low: currentLow,
          open: currentOpen,
          updated: ticker.updated,
          prevClose: prevClose,
          prevVolume: prevVolume,
          preMarket: preMarket,
          preMarketChange: preMarketChange,
          preMarketChangePercent: preMarketChangePercent,
          afterHours: afterHours,
          afterHoursChange: afterHoursChange,
          afterHoursChangePercent: afterHoursChangePercent,
        };
      });

      setState({
        data: displayData,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch ticker data',
      }));
    }
  }, [isAuthenticated]); // Include isAuthenticated to switch APIs when auth state changes

  // Fetch data on mount and when tickers change
  useEffect(() => {
    console.log('📡 useEffect triggered - fetching data for:', tickersString);
    fetchTickerData();
  }, [tickersString, fetchTickerData]);

  // Helper function to check if data is fresh (less than 15 minutes old)
  const isDataFresh = useCallback((tickerData: TickerDisplayData[]): boolean => {
    if (!tickerData || tickerData.length === 0) return false;
    
    // Check the most recent updated timestamp from the ticker data
    const mostRecentUpdate = Math.max(...tickerData.map(ticker => {
      // Convert from nanoseconds to milliseconds if needed
      let timestampInMs = ticker.updated;
      if (ticker.updated > 1e15) { // If timestamp is larger than 1e15, it's likely in nanoseconds
        timestampInMs = ticker.updated / 1e6; // Convert nanoseconds to milliseconds
      }
      return timestampInMs;
    }));
    
    const now = new Date();
    const timeDifferenceMs = now.getTime() - mostRecentUpdate;
    const timeDifferenceMinutes = timeDifferenceMs / (1000 * 60);
    
    // Debug logging
    console.log('🔍 Fresh data check:', {
      mostRecentUpdate,
      currentTime: now.getTime(),
      timeDifferenceMs,
      timeDifferenceMinutes,
      isFresh: timeDifferenceMinutes < 15
    });
    
    // If the timestamp is in the future, consider it fresh
    if (timeDifferenceMs < 0) {
      console.log('📅 Timestamp is in the future, considering data fresh');
      return true;
    }
    
    return timeDifferenceMinutes < 30; // Less than 30 minutes old
  }, []);

  // Auto-refresh every 30 seconds with session management and data freshness check
  useEffect(() => {
    let interval: NodeJS.Timeout;
    let lastActivity = Date.now();
    const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes of inactivity
    const REFRESH_INTERVAL = 30 * 1000; // 30 seconds

    // Track user activity
    const updateActivity = () => {
      lastActivity = Date.now();
    };

    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    // Set up auto-refresh interval
    interval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      const dataIsFresh = isDataFresh(state.data);
      const marketIsOpen = marketStatus !== 'closed';
      
      if (timeSinceLastActivity < SESSION_TIMEOUT && dataIsFresh && marketIsOpen) {
        console.log('⏰ Auto-refresh triggered (user active, data fresh, market open)');
        fetchTickerData();
      } else if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        console.log('⏰ Auto-refresh skipped (user inactive for', Math.round(timeSinceLastActivity / 1000), 'seconds)');
      } else if (!marketIsOpen) {
        console.log('⏰ Auto-refresh skipped (market is closed)');
      } else {
        console.log('⏰ Auto-refresh skipped (data is stale, >15 minutes old)');
      }
    }, REFRESH_INTERVAL);

    console.log('⏰ Auto-refresh interval started (30s, stops after 5min inactivity or when data is stale)');

    return () => {
      console.log('⏰ Clearing auto-refresh interval and event listeners');
      clearInterval(interval);
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [fetchTickerData, isDataFresh, state.data, marketStatus]);

  // Determine if refresh should be available (data is fresh - when we can refresh)
  const shouldShowRefresh = isDataFresh(state.data);

  return {
    ...state,
    refetch: fetchTickerData,
    shouldShowRefresh,
  };
};
